import React, { useEffect, useRef, useState } from 'react';
import { User, Home, Calculator, Wrench, MessageSquare, ExternalLink, Users, ChevronDown, ChevronUp } from 'lucide-react';
import ClientDetailsConfirm from '@/pages/workflow/renderers/ClientDetailsConfirm';
import PropertyDetailsConfirm from '@/pages/workflow/renderers/PropertyDetailsConfirm';
import TenantAgentConfirm from '@/pages/workflow/renderers/TenantAgentConfirm';

const SUBTAB = (cat) => {
  const s = (cat || '').toLowerCase();
  if (/re-?\s*roof|new roof/.test(s)) return 'reroof';
  if (/coat|paint/.test(s)) return 'painting';
  if (/gutter|spout/.test(s)) return 'guttering';
  if (/repair|rescrew|maint/.test(s)) return 'repairs';
  return '';
};

// Focused pop-out that calls onClosed when the operator hits Save & Return (window closes).
function usePopout(onClosed) {
  const timers = useRef({});
  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);
  return (url, name) => {
    const w = window.open(url, name, 'width=1400,height=900,scrollbars=yes,resizable=yes,popup=1');
    if (!w) { window.open(url, '_blank'); return; }
    clearInterval(timers.current[name]);
    timers.current[name] = setInterval(() => {
      if (w.closed) { clearInterval(timers.current[name]); delete timers.current[name]; onClosed(); }
    }, 500);
  };
}

const Card = ({ icon: Icon, title, hint, children, testId }) => (
  <div className="bg-white border border-[#E2E2DF] rounded-md p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1"><Icon size={15} className="text-[#D35400]" /><div className="text-sm font-bold text-slate-800">{title}</div></div>
    <p className="text-[11px] text-slate-500 mb-3">{hint}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
const Btn = ({ onClick, children, testId, icon = true }) => (
  <button data-testid={testId} onClick={onClick} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#E2E2DF] bg-white hover:border-[#0F4F1B] hover:text-[#0F4F1B] transition-colors">
    {children} {icon && <ExternalLink size={11} />}
  </button>
);

const INLINE = {
  client: [ClientDetailsConfirm, 'Client details'],
  property: [PropertyDetailsConfirm, 'Property details'],
  tenant: [TenantAgentConfirm, 'Tenant / agent'],
};

function InlineForm({ kind, snap, onDone }) {
  const [Comp] = INLINE[kind];
  return (
    <div className="bg-white border-2 border-sky-200 rounded-md p-4" data-testid={`ur-inline-${kind}`}>
      <Comp action={{ id: `ur-${kind}`, params: {} }} capturedData={{ client_id: snap.client.id, property_id: snap.property.id }}
        canBack={false} onBack={() => {}} onCancel={() => onDone(false)} onNext={() => onDone(true)} continueLabel="Save & return ↩" />
    </div>
  );
}

// Step 2 — focused update cards: contact details inline, builder / pricing / wording in locked pop-outs.
export const UpdateCards = ({ snap, onRefresh }) => {
  const [open, setOpen] = useState('');
  const pop = usePopout(onRefresh);
  const toggle = (k) => setOpen(open === k ? '' : k);
  const Toggle = ({ k, testId, children }) => (
    <Btn testId={testId} icon={false} onClick={() => toggle(k)}>{children} {open === k ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</Btn>
  );
  return (
    <div className="space-y-3" data-testid="ur-update-cards">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card icon={User} title="Client details" hint="Names, spelling, contact details — edits here only." testId="ur-card-client">
          <Toggle k="client" testId="ur-open-client">Edit {snap.client.name}</Toggle>
        </Card>
        <Card icon={Home} title="Property details" hint="Address, property type, tenant / agent." testId="ur-card-property">
          <Toggle k="property" testId="ur-open-property">Edit {snap.property.address || 'property'}</Toggle>
          <Toggle k="tenant" testId="ur-open-tenant"><Users size={11} />Tenant / agent</Toggle>
        </Card>
        <Card icon={Wrench} title="Quote work (builds)" hint="Opens just the builder for that quote." testId="ur-card-builds">
          {snap.quotes.filter((q) => SUBTAB(q.category)).length === 0 && <span className="text-[11px] text-slate-400">No builder on this job — use pricing below or start a new build from the job.</span>}
          {snap.quotes.filter((q) => SUBTAB(q.category)).map((q) => (
            <Btn key={q.id} testId={`ur-open-builder-${q.appraisal_no}`}
              onClick={() => pop(`/appraisals/${q.id}?embed=quote_builder&builder=${SUBTAB(q.category)}`, `ur-b-${q.id}`)}>
              A{q.appraisal_no} {q.quotation_type || q.category}
            </Btn>
          ))}
        </Card>
        <Card icon={Calculator} title="Discount & pricing" hint="Opens only the pricing panel for the quote being resent." testId="ur-card-pricing">
          {snap.quotes.map((q) => <Btn key={q.id} testId={`ur-open-pricing-${q.appraisal_no}`} onClick={() => pop(`/appraisals/${q.id}?embed=pricing`, `ur-p-${q.id}`)}>A{q.appraisal_no} pricing</Btn>)}
        </Card>
        <Card icon={MessageSquare} title="Wording & comments" hint="Recommended work, roof condition and comments that print on the documents." testId="ur-card-wording">
          <Btn testId="ur-open-job" onClick={() => pop(`/appraisals/${snap.job.id}?embed=wording`, 'ur-job')}>Wording A{snap.job.appraisal_no}</Btn>
        </Card>
      </div>
      {open && <InlineForm key={open} kind={open} snap={snap} onDone={(saved) => { setOpen(''); if (saved) onRefresh(); }} />}
    </div>
  );
};
